/**
 * Persistent Registration & OTP Session Manager
 * Ensures that when users leave the app/tab to check OTP emails (e.g. Gmail/Outlook on mobile or desktop)
 * and return, their registration form data, OTP screen, and timer are fully preserved across tab sleeping,
 * memory discards, and page reloads.
 */

const STORAGE_KEY = "zt_pending_auth_session";
const MODAL_KEY = "zt_auth_modal_active";
const MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes (matching standard 5-10 min OTP lifespan)

export function savePendingAuthSession(data) {
  try {
    const payload = {
      ...data,
      savedAt: Date.now(),
    };
    const serialized = JSON.stringify(payload);
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(STORAGE_KEY, serialized);
    }
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, serialized);
    }
  } catch (e) {
    console.warn("Failed to save auth session:", e);
  }
}

export function getPendingAuthSession() {
  try {
    let raw = null;
    if (typeof sessionStorage !== "undefined") {
      raw = sessionStorage.getItem(STORAGE_KEY);
    }
    if (!raw && typeof localStorage !== "undefined") {
      raw = localStorage.getItem(STORAGE_KEY);
    }
    if (!raw) return null;

    const data = JSON.parse(raw);
    if (!data || !data.savedAt) return null;

    // Expire if older than MAX_AGE_MS
    if (Date.now() - data.savedAt > MAX_AGE_MS) {
      clearPendingAuthSession();
      return null;
    }

    return data;
  } catch (e) {
    return null;
  }
}

export function clearPendingAuthSession() {
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(MODAL_KEY);
    }
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(MODAL_KEY);
    }
  } catch (e) {}
}

export function setAuthModalActive(isOpen, mode = "login") {
  try {
    if (isOpen) {
      const val = JSON.stringify({ mode, timestamp: Date.now() });
      sessionStorage?.setItem(MODAL_KEY, val);
    } else {
      sessionStorage?.removeItem(MODAL_KEY);
    }
  } catch (e) {}
}

export function getAuthModalActive() {
  try {
    const raw = sessionStorage?.getItem(MODAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - (parsed.timestamp || 0) > MAX_AGE_MS) {
      sessionStorage?.removeItem(MODAL_KEY);
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
}
